import "@dxos/node-std/globals"

// packages/core/mesh/rpc/src/rpc.ts
import assert from "@dxos/node-std/assert";
import { asyncTimeout, synchronized, Trigger } from "@dxos/async";
import { Stream } from "@dxos/codec-protobuf";
import { StackTrace } from "@dxos/debug";
import { log } from "@dxos/log";
import { schema } from "@dxos/protocols";
import { exponentialBackoffInterval } from "@dxos/util";

// packages/core/mesh/rpc/src/errors.ts
var SerializedRpcError = class extends Error {
  // prettier-ignore
  constructor(name, message) {
    super(message);
    this.name = name;
  }
};
var preprocessStack = (stack) => {
  const match = /^\s+at/gm.exec(stack);
  if (!match) {
    return stack;
  }
  return stack.slice(match.index);
};
var RpcClosedError = class extends Error {
  constructor() {
    super("Request was terminated because the RPC endpoint is closed.");
  }
};
var RpcNotOpenError = class extends Error {
  constructor() {
    super("RPC has not been opened.");
  }
};
var encodeError = (err) => {
  if (typeof err === "object" && (err == null ? void 0 : err.message)) {
    return {
      name: err.name,
      message: err.message,
      stack: err.stack
    };
  } else if (typeof err === "string") {
    return {
      message: err
    };
  } else {
    return {
      message: JSON.stringify(err)
    };
  }
};
var decodeError = (err, rpcMethod) => {
  var _a, _b, _c;
  let error;
  switch (err.name) {
    case "RpcClosedError":
      error = new RpcClosedError();
      break;
    case "RpcNotOpenError":
      error = new RpcNotOpenError();
      break;
    default:
      error = new SerializedRpcError((_a = err.name) != null ? _a : "Error", (_b = err.message) != null ? _b : "Unknown Error");
  }
  error.stack = ((_c = err.stack) != null ? _c : "") + `
    at RPC call: ${rpcMethod} 
` + preprocessStack(error.stack);
  return error;
};

// packages/core/mesh/rpc/src/rpc.ts
var __decorate = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var DEFAULT_TIMEOUT = 3e3;
var PendingRpcRequest = class {
  constructor(resolve, reject, stream) {
    this.resolve = resolve;
    this.reject = reject;
    this.stream = stream;
  }
};
var codec = schema.getCodecForType("dxos.rpc.RpcMessage");
var RpcPeer = class {
  // prettier-ignore
  constructor(_options) {
    this._options = _options;
    this._outgoingRequests = /* @__PURE__ */ new Map();
    this._localStreams = /* @__PURE__ */ new Map();
    this._remoteOpenTrigger = new Trigger();
    this._closeTrigger = new Trigger();
    this._nextId = 0;
    this._open = false;
  }
  /**
  * Open the peer. Required before making any calls.
  *
  * Will block before the other peer calls `open`.
  */
  async open() {
    var _a;
    if (this._open) {
      return;
    }
    this._unsubscribe = this._options.port.subscribe(async (msg) => {
      try {
        await this._receive(msg);
      } catch (err) {
        log.catch(err, {}, {
          file: "rpc.ts",
          line: 98,
          scope: this,
          callSite: (f, a) => f(...a)
        });
      }
    });
    this._open = true;
    if (this._options.noHandshake) {
      this._remoteOpenTrigger.wake();
      return;
    }
    log("sending open message", {}, {
      file: "rpc.ts",
      line: 109,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._sendMessage({
      open: true
    });
    this._clearOpenInterval = exponentialBackoffInterval(() => {
      void this._sendMessage({
        open: true
      }).catch((err) => log.warn(err, {}, {
        file: "rpc.ts",
        line: 114,
        scope: this,
        callSite: (f, a) => f(...a)
      }));
    }, 50);
    await Promise.race([
      this._remoteOpenTrigger.wait(),
      this._closeTrigger.wait()
    ]);
    (_a = this._clearOpenInterval) == null ? void 0 : _a.call(this);
    if (!this._open) {
      return;
    }
    log("sending second open message", {}, {
      file: "rpc.ts",
      line: 128,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    await this._sendMessage({
      openAck: true
    });
  }
  /**
  * Close the peer. Stop taking or making requests.
  */
  async close() {
    var _a, _b;
    (_a = this._unsubscribe) == null ? void 0 : _a.call(this);
    (_b = this._clearOpenInterval) == null ? void 0 : _b.call(this);
    this._closeTrigger.wake();
    for (const req of this._outgoingRequests.values()) {
      req.reject(new RpcClosedError());
    }
    this._outgoingRequests.clear();
    this._open = false;
  }
  /**
  * Handle incoming message. Should be called as the result of other peer's `send` callback.
  */
  async _receive(msg) {
    var _a, _b;
    const decoded = codec.decode(msg, {
      preserveAny: true
    });
    if (decoded.request) {
      if (!this._open) {
        log("received request while closed", {}, {
          file: "rpc.ts",
          line: 154,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        await this._sendMessage({
          response: {
            id: decoded.request.id,
            error: encodeError(new RpcClosedError())
          }
        });
        return;
      }
      const req = decoded.request;
      if (req.stream) {
        log("stream request", {
          method: req.method
        }, {
          file: "rpc.ts",
          line: 166,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        this._callStreamHandler(req, (response) => {
          var _a2;
          log("sending stream response", {
            method: req.method,
            response: (_a2 = response.payload) == null ? void 0 : _a2.type_url,
            error: response.error,
            close: response.close
          }, {
            file: "rpc.ts",
            line: 168,
            scope: this,
            callSite: (f, a) => f(...a)
          });
          void this._sendMessage({
            response
          }).catch((err) => {
            log.warn("failed during close", err, {
              file: "rpc.ts",
              line: 176,
              scope: this,
              callSite: (f, a) => f(...a)
            });
          });
        });
      } else {
        log("request", {
          method: req.method
        }, {
          file: "rpc.ts",
          line: 180,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        const response = await this._callHandler(req);
        log("sending response", {
          method: req.method,
          response: (_a = response.payload) == null ? void 0 : _a.type_url,
          error: response.error
        }, {
          file: "rpc.ts",
          line: 183,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        await this._sendMessage({
          response
        });
      }
    } else if (decoded.response) {
      if (!this._open) {
        log("received response while closed", {}, {
          file: "rpc.ts",
          line: 192,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return;
      }
      const responseId = decoded.response.id;
      assert(typeof responseId === "number");
      if (!this._outgoingRequests.has(responseId)) {
        log("received response with invalid id", {
          responseId
        }, {
          file: "rpc.ts",
          line: 199,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return;
      }
      const item = this._outgoingRequests.get(responseId);
      if (!item.stream) {
        this._outgoingRequests.delete(responseId);
      }
      log("response", {
        type_url: (_b = decoded.response.payload) == null ? void 0 : _b.type_url
      }, {
        file: "rpc.ts",
        line: 209,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      item.resolve(decoded.response);
    } else if (decoded.open) {
      log("received open message", {}, {
        file: "rpc.ts",
        line: 212,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      if (this._options.noHandshake) {
        return;
      }
      await this._sendMessage({
        openAck: true
      });
    } else if (decoded.openAck) {
      log("received openAck message", {}, {
        file: "rpc.ts",
        line: 219,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      if (this._options.noHandshake) {
        return;
      }
      this._remoteOpenTrigger.wake();
    } else if (decoded.streamClose) {
      if (!this._open) {
        log("received stream close while closed", {}, {
          file: "rpc.ts",
          line: 227,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return;
      }
      log("received stream close", {
        id: decoded.streamClose.id
      }, {
        file: "rpc.ts",
        line: 231,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      assert(typeof decoded.streamClose.id === "number");
      const stream = this._localStreams.get(decoded.streamClose.id);
      if (!stream) {
        log("no local stream", {
          id: decoded.streamClose.id
        }, {
          file: "rpc.ts",
          line: 235,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return;
      }
      this._localStreams.delete(decoded.streamClose.id);
      stream.close();
    } else {
      log.error("received malformed message", {
        msg
      }, {
        file: "rpc.ts",
        line: 242,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      throw new Error("Malformed message.");
    }
  }
  /**
  * Make RPC call. Will trigger a handler on the other side.
  * Peer should be open before making this call.
  */
  async call(method, request) {
    var _a, _b;
    log("calling", {
      method
    }, {
      file: "rpc.ts",
      line: 252,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    if (!this._open) {
      throw new RpcNotOpenError();
    }
    let response;
    try {
      const id = this._nextId++;
      const responseReceived = new Promise((resolve, reject) => {
        this._outgoingRequests.set(id, new PendingRpcRequest(resolve, reject, false));
      });
      const sending = this._sendMessage({
        request: {
          id,
          method,
          payload: request,
          stream: false
        }
      });
      const waiting = asyncTimeout(responseReceived, (_a = this._options.timeout) != null ? _a : DEFAULT_TIMEOUT);
      await Promise.race([
        sending,
        waiting
      ]);
      response = await waiting;
      assert(response.id === id);
    } catch (err) {
      if (err instanceof RpcClosedError) {
        const error = new RpcClosedError();
        error.stack += `

 info: RPC client was closed at:
${(_b = err.stack) == null ? void 0 : _b.split("\n").slice(1).join("\n")}`;
        throw error;
      }
      throw err;
    }
    if (response.payload) {
      return response.payload;
    } else if (response.error) {
      throw decodeError(response.error, method);
    } else {
      throw new Error("Malformed response.");
    }
  }
  /**
  * Make RPC call with a streaming response.
  * Will trigger a handler on the other side.
  * Peer should be open before making this call.
  */
  callStream(method, request) {
    if (!this._open) {
      throw new RpcNotOpenError();
    }
    const id = this._nextId++;
    return new Stream(({ ready, next, close }) => {
      const onResponse = (response) => {
        if (response.streamReady) {
          ready();
        } else if (response.close) {
          close();
        } else if (response.error) {
          close(decodeError(response.error, method));
        } else if (response.payload) {
          next(response.payload);
        } else {
          throw new Error("Malformed response.");
        }
      };
      const stack = new StackTrace();
      const closeStream = (err) => {
        if (!err) {
          close();
        } else {
          err.stack += `

Error happened in the stream at:
${stack.getStack()}`;
          close(err);
        }
      };
      this._outgoingRequests.set(id, new PendingRpcRequest(onResponse, closeStream, true));
      this._sendMessage({
        request: {
          id,
          method,
          payload: request,
          stream: true
        }
      }).catch((err) => {
        close(err);
      });
      return () => {
        this._sendMessage({
          streamClose: {
            id
          }
        }).catch((err) => {
          log.catch(err, {}, {
            file: "rpc.ts",
            line: 355,
            scope: this,
            callSite: (f, a) => f(...a)
          });
        });
      };
    });
  }
  async _sendMessage(message) {
    await this._options.port.send(codec.encode(message, {
      preserveAny: true
    }));
  }
  async _callHandler(req) {
    try {
      assert(typeof req.id === "number");
      assert(req.payload);
      assert(req.method);
      const response = await this._options.callHandler(req.method, req.payload);
      return {
        id: req.id,
        payload: response
      };
    } catch (err) {
      return {
        id: req.id,
        error: encodeError(err)
      };
    }
  }
  _callStreamHandler(req, callback) {
    try {
      assert(this._options.streamHandler, "Requests with streaming responses are not supported.");
      assert(typeof req.id === "number");
      assert(req.payload);
      assert(req.method);
      const responseStream = this._options.streamHandler(req.method, req.payload);
      responseStream.onReady(() => {
        callback({
          id: req.id,
          streamReady: true
        });
      });
      responseStream.subscribe((msg) => {
        callback({
          id: req.id,
          payload: msg
        });
      }, (error) => {
        if (error) {
          callback({
            id: req.id,
            error: encodeError(error)
          });
        } else {
          callback({
            id: req.id,
            close: true
          });
        }
      });
      this._localStreams.set(req.id, responseStream);
    } catch (err) {
      callback({
        id: req.id,
        error: encodeError(err)
      });
    }
  }
};
__decorate([
  synchronized
], RpcPeer.prototype, "open", null);

// packages/core/mesh/rpc/src/service.ts
import assert2 from "@dxos/node-std/assert";
var createServiceBundle = (services) => services;
var ProtoRpcPeer = class {
  // prettier-ignore
  constructor(rpc, _peer) {
    this.rpc = rpc;
    this._peer = _peer;
  }
  async open() {
    await this._peer.open();
  }
  async close() {
    await this._peer.close();
  }
};
var createProtoRpcPeer = ({ requested, exposed, handlers, encodingOptions, ...rest }) => {
  const exposedRpcs = {};
  if (exposed) {
    assert2(handlers);
    for (const serviceName of Object.keys(exposed)) {
      const serviceFqn = exposed[serviceName].serviceProto.fullName.slice(1);
      const serviceProvider = handlers[serviceName];
      exposedRpcs[serviceFqn] = exposed[serviceName].createServer(serviceProvider, encodingOptions);
    }
  }
  const peer = new RpcPeer({
    ...rest,
    callHandler: (method, request) => {
      const [serviceName, methodName] = parseMethodName(method);
      if (!exposedRpcs[serviceName]) {
        throw new Error(`Service not supported: ${serviceName}`);
      }
      return exposedRpcs[serviceName].call(methodName, request);
    },
    streamHandler: (method, request) => {
      const [serviceName, methodName] = parseMethodName(method);
      if (!exposedRpcs[serviceName]) {
        throw new Error(`Service not supported: ${serviceName}`);
      }
      return exposedRpcs[serviceName].callStream(methodName, request);
    }
  });
  const requestedRpcs = {};
  if (requested) {
    for (const serviceName1 of Object.keys(requested)) {
      const serviceFqn1 = requested[serviceName1].serviceProto.fullName.slice(1);
      requestedRpcs[serviceName1] = requested[serviceName1].createClient({
        call: (method, req) => peer.call(`${serviceFqn1}.${method}`, req),
        callStream: (method, req) => peer.callStream(`${serviceFqn1}.${method}`, req)
      }, encodingOptions);
    }
  }
  return new ProtoRpcPeer(requestedRpcs, peer);
};
var parseMethodName = (method) => {
  const separator = method.lastIndexOf(".");
  const serviceName = method.slice(0, separator);
  const methodName = method.slice(separator + 1);
  if (serviceName.length === 0 || methodName.length === 0) {
    throw new Error(`Invalid method: ${method}`);
  }
  return [
    serviceName,
    methodName
  ];
};
var createRpcClient = (serviceDef, options) => {
  const peer = new RpcPeer({
    ...options,
    callHandler: () => {
      throw new Error("Requests to client are not supported.");
    }
  });
  const client = serviceDef.createClient({
    call: peer.call.bind(peer),
    callStream: peer.callStream.bind(peer)
  });
  return new ProtoRpcPeer(client, peer);
};
var createRpcServer = ({ service, handlers, ...rest }) => {
  const server = service.createServer(handlers);
  return new RpcPeer({
    ...rest,
    callHandler: server.call.bind(server),
    streamHandler: server.callStream.bind(server)
  });
};
var createBundledRpcClient = (descriptors, options) => {
  return createProtoRpcPeer({
    requested: descriptors,
    ...options
  });
};
var createBundledRpcServer = ({ services, handlers, ...rest }) => {
  const rpc = {};
  for (const serviceName of Object.keys(services)) {
    const serviceFqn = services[serviceName].serviceProto.fullName.slice(1);
    rpc[serviceFqn] = services[serviceName].createServer(handlers[serviceName]);
  }
  return new RpcPeer({
    ...rest,
    callHandler: (method, request) => {
      const [serviceName, methodName] = parseMethodName(method);
      if (!rpc[serviceName]) {
        throw new Error(`Service not supported: ${serviceName}`);
      }
      return rpc[serviceName].call(methodName, request);
    },
    streamHandler: (method, request) => {
      const [serviceName, methodName] = parseMethodName(method);
      if (!rpc[serviceName]) {
        throw new Error(`Service not supported: ${serviceName}`);
      }
      return rpc[serviceName].callStream(methodName, request);
    }
  });
};

// packages/core/mesh/rpc/src/testing.ts
var createLinkedPorts = () => {
  let port1Received;
  let port2Received;
  const port1 = {
    send: (msg) => port2Received == null ? void 0 : port2Received(msg),
    subscribe: (cb) => {
      port1Received = cb;
    }
  };
  const port2 = {
    send: (msg) => port1Received == null ? void 0 : port1Received(msg),
    subscribe: (cb) => {
      port2Received = cb;
    }
  };
  return [
    port1,
    port2
  ];
};

// packages/core/mesh/rpc/src/trace.ts
import { Event } from "@dxos/async";
import { MessageTrace } from "@dxos/protocols/proto/dxos/rpc";
var PortTracer = class {
  // prettier-ignore
  constructor(_wrappedPort) {
    this._wrappedPort = _wrappedPort;
    this.message = new Event();
    this._port = {
      send: (msg) => {
        this.message.emit({
          direction: MessageTrace.Direction.OUTGOING,
          data: msg
        });
        return this._wrappedPort.send(msg);
      },
      subscribe: (cb) => {
        return this._wrappedPort.subscribe((msg) => {
          this.message.emit({
            direction: MessageTrace.Direction.INCOMING,
            data: msg
          });
          cb(msg);
        });
      }
    };
  }
  get port() {
    return this._port;
  }
};
export {
  PortTracer,
  ProtoRpcPeer,
  RpcClosedError,
  RpcNotOpenError,
  RpcPeer,
  SerializedRpcError,
  createBundledRpcClient,
  createBundledRpcServer,
  createLinkedPorts,
  createProtoRpcPeer,
  createRpcClient,
  createRpcServer,
  createServiceBundle,
  decodeError,
  encodeError,
  parseMethodName
};
//# sourceMappingURL=index.mjs.map
